import type { FormEvent } from 'react'
import { useEffect, useState } from 'react'
import LegalLayout from './LegalLayout'

type SubmissionStatus = 'idle' | 'success' | 'error'

// Use relative path to the API route that the backend exposes
// (controller is mounted at /factur_247/derechos-arco/procesar_formulario)
const SUBMIT_ENDPOINT = '/factur_247/derechos-arco/procesar_formulario'

function DerechosArco() {
    const [status, setStatus] = useState<SubmissionStatus>('idle')
    const [isSubmitting, setIsSubmitting] = useState(false)

    useEffect(() => {
        if (status === 'idle') {
            return undefined
        }

        const timer = window.setTimeout(() => setStatus('idle'), 5000)
        return () => window.clearTimeout(timer)
    }, [status])

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        const form = event.currentTarget
        const formData = new FormData(form)
        const payload: Record<string, FormDataEntryValue> = Object.fromEntries(formData.entries())

        try {
            setIsSubmitting(true)
            setStatus('idle')

            const response = await fetch(SUBMIT_ENDPOINT, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                },
                body: JSON.stringify(payload),
            })

            let data: any = {}
            try {
                data = await response.json()
            } catch (err) {
                // not JSON response
                data = {}
            }

            if (!response.ok) {
                const message = data && data.message ? data.message : `HTTP ${response.status}`
                throw new Error(message)
            }

            if (data && data.success === false) {
                throw new Error(data.message ?? 'Error en la solicitud')
            }

            form.reset()
            setStatus('success')
        } catch (error) {
            console.error('Error al enviar el formulario ARCO', error)
            setStatus('error')
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <LegalLayout title="Formulario de Ejercicio de Derechos ARCO" subtitle="Factur 24/7">
            <section className="legal-section">
                <div className="legal-info-box">
                    <p>
                        Complete este formulario para ejercer sus derechos de Acceso, Rectificación, Cancelación u Oposición (ARCO)
                        sobre sus datos personales, de acuerdo con la Ley Orgánica de Protección de Datos Personales del Ecuador
                        (LOPDP).
                    </p>
                    <p>
                        Los campos marcados con <span style={{ color: 'red' }}>*</span> son obligatorios.
                    </p>
                </div>

                <form className="legal-form" onSubmit={handleSubmit}>
                    <div className="legal-form-group">
                        <label htmlFor="nombre" className="legal-required">
                            Nombre y Apellido
                        </label>
                        <input type="text" id="nombre" name="nombre" required />
                    </div>

                    <div className="legal-form-group">
                        <label htmlFor="cedula" className="legal-required">
                            Cédula
                        </label>
                        <input
                            type="text"
                            id="cedula"
                            name="cedula"
                            pattern="[0-9]{10}"
                            title="Ingrese un número de cédula válido de 10 dígitos"
                            required
                        />
                    </div>

                    <div className="legal-form-group">
                        <label htmlFor="telefono" className="legal-required">
                            Número de Teléfono
                        </label>
                        <input
                            type="tel"
                            id="telefono"
                            name="telefono"
                            pattern="[0-9]{10}"
                            title="Ingrese un número de teléfono válido de 10 dígitos"
                            required
                        />
                    </div>

                    <div className="legal-form-group">
                        <label htmlFor="emailNuevo" className="legal-required">
                            Correo Electrónico Nuevo
                        </label>
                        <input type="email" id="emailNuevo" name="emailNuevo" required />
                    </div>

                    <div className="legal-form-group">
                        <label htmlFor="emailAntiguo" className="legal-required">
                            Correo Electrónico Antiguo
                        </label>
                        <input type="email" id="emailAntiguo" name="emailAntiguo" required />
                    </div>

                    <div className="legal-form-group">
                        <label htmlFor="derecho" className="legal-required">
                            Derecho a ejercer
                        </label>
                        <select id="derecho" name="derecho" required>
                            <option value="">Seleccione una opción</option>
                            <option value="Acceso">Acceso - Conocer qué datos míos están siendo tratados</option>
                            <option value="Rectificación">Rectificación - Corregir datos inexactos o incompletos</option>
                            <option value="Cancelación">Cancelación - Eliminar mis datos personales</option>
                            <option value="Oposición">Oposición - Impedir el tratamiento de mis datos</option>
                        </select>
                    </div>

                    <div className="legal-form-group">
                        <label htmlFor="detalles">Detalles adicionales (opcional)</label>
                        <textarea id="detalles" name="detalles" rows={4}></textarea>
                    </div>

                    <button type="submit" disabled={isSubmitting}>
                        {isSubmitting ? 'Enviando…' : 'Enviar Solicitud'}
                    </button>
                </form>

                {status === 'success' && (
                    <div className="legal-alert legal-alert-success" role="status">
                        <p>Su solicitud ha sido enviada correctamente. Nos pondremos en contacto con usted a la brevedad posible.</p>
                    </div>
                )}

                {status === 'error' && (
                    <div className="legal-alert legal-alert-error" role="alert">
                        <p>
                            Ha ocurrido un error al enviar su solicitud. Por favor, intente nuevamente más tarde o contáctenos
                            directamente por correo electrónico.
                        </p>
                    </div>
                )}
            </section>
        </LegalLayout>
    )
}

export default DerechosArco
